import React, { useEffect, useState, useRef, useCallback } from 'react';
import { connect } from 'react-redux';
import { Link } from 'react-router-dom';
import { FaEye, FaBook, FaRoute, FaUsers, FaChartLine, FaCalendarAlt } from 'react-icons/fa';
import StudentDashboard from './StudentDashboard';
import DashboardSidebar from './DashboardSidebar';
import StudentProgressTab from './StudentProgressTab';
import axios from 'axios';

// ─── Count-up animation hook ────────────────────────────────────────────────
const useCountUp = (target, duration = 1200) => {
  const [value, setValue] = useState(0);
  const raf = useRef(null);

  useEffect(() => {
    if (target === null || target === undefined) return;
    const start = performance.now();
    const animate = (now) => {
      const progress = Math.min((now - start) / duration, 1);
      const eased = 1 - Math.pow(1 - progress, 3);
      setValue(Math.round(eased * target));
      if (progress < 1) raf.current = requestAnimationFrame(animate);
    };
    raf.current = requestAnimationFrame(animate);
    return () => cancelAnimationFrame(raf.current);
  }, [target, duration]);

  return value;
};

// ─── Reusable content list with Load More ───────────────────────────────────
const ContentList = ({ endpoint, viewPath, icon: Icon, label, authToken }) => {
  const [items, setItems]       = useState([]);
  const [page, setPage]         = useState(1);
  const [hasMore, setHasMore]   = useState(false);
  const [loading, setLoading]   = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const initialFetched = useRef(false);

  const fetchPage = useCallback((pageNum, append = false) => {
    if (append) setLoadingMore(true); else setLoading(true);

    axios
      .get(`${endpoint}?page=${pageNum}&limit=10`, {
        headers: { Authorization: `Bearer ${authToken}` }
      })
      .then(res => {
        const data = res.data;
        // list endpoints return { lessons/courses/paths: [], hasNextPage, ... }
        const records = data.lessons || data.courses || data.paths || [];
        setItems(prev => append ? [...prev, ...records] : records);
        setHasMore(data.hasNextPage || false);
        setPage(pageNum);
      })
      .catch(() => {})
      .finally(() => { setLoading(false); setLoadingMore(false); });
  }, [endpoint, authToken]);

  useEffect(() => {
    if (!initialFetched.current) {
      initialFetched.current = true;
      fetchPage(1);
    }
  }, [fetchPage]);

  if (loading) {
    return <p className="text-gray-500 text-sm py-4">Loading...</p>;
  }

  return (
    <div>
      {items.length === 0 ? (
        <p className="text-gray-500 text-sm py-4">No {label.toLowerCase()} found.</p>
      ) : (
        <ul className="space-y-2">
          {items.map(item => (
            <li
              key={item._id}
              className="flex items-center justify-between px-4 py-3 bg-gray-50 rounded-lg border border-gray-200 hover:border-[#1086a4] transition-colors"
            >
              <div className="flex items-center gap-3 min-w-0">
                <Icon className="text-[#1086a4] shrink-0" />
                <span className="text-gray-700 text-sm truncate">{item.title}</span>
              </div>
              <Link
                to={`${viewPath}/${item._id}/view`}
                className="text-[#1086a4] hover:text-[#054581] transition-colors ml-3 shrink-0"
                title={`View ${label}`}
              >
                <FaEye />
              </Link>
            </li>
          ))}
        </ul>
      )}

      {hasMore && (
        <button
          onClick={() => fetchPage(page + 1, true)}
          disabled={loadingMore}
          className="mt-4 w-full py-2 text-sm font-medium text-[#1086a4] border border-[#1086a4] rounded-lg hover:bg-[#1086a4] hover:text-white transition-colors disabled:opacity-50"
        >
          {loadingMore ? 'Loading...' : 'Load More'}
        </button>
      )}
    </div>
  );
};

// ─── Main component ──────────────────────────────────────────────────────────
const DashboardContainer = ({ auth: { user, token: authToken } }) => {
  const [activeTab, setActiveTab]       = useState('Overview');
  const [activeContent, setActiveContent] = useState('Lessons');
  const [counts, setCounts]             = useState(null);

  const animatedLessons = useCountUp(counts?.lessons ?? null);
  const animatedCourses = useCountUp(counts?.courses ?? null);
  const animatedPaths   = useCountUp(counts?.paths   ?? null);

  useEffect(() => {
    axios
      .get('/api/dashboard/counts', { headers: { Authorization: `Bearer ${authToken}` } })
      .then(res => setCounts(res.data))
      .catch(() => setCounts(null));
  }, [authToken]);

  if (user && user.role === 'student') return <StudentDashboard />;

  const contentTabs = [
    { key: 'Lessons',  label: 'Lessons',        icon: FaBook,  endpoint: '/api/lessons/list',  viewPath: '/content-management/lessons' },
    { key: 'Courses',  label: 'Courses',         icon: FaRoute, endpoint: '/api/courses/list',  viewPath: '/content-management/courses' },
    { key: 'Learnings',label: 'Learning Paths',  icon: FaUsers, endpoint: '/api/paths/list',    viewPath: '/content-management/paths'   },
  ];

  const renderOverview = () => (
    <div className="space-y-6">
      <h2 className="text-2xl font-bold text-[#054581]">Welcome to Your Dashboard</h2>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
          <div className="flex items-center">
            <FaBook className="text-[#1086a4] text-2xl mr-4" />
            <div>
              <h3 className="text-sm font-medium text-gray-600">Total Lessons</h3>
              <p className="text-2xl font-bold text-[#054581]">{counts ? animatedLessons : '—'}</p>
            </div>
          </div>
        </div>

        <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
          <div className="flex items-center">
            <FaRoute className="text-[#1086a4] text-2xl mr-4" />
            <div>
              <h3 className="text-sm font-medium text-gray-600">Total Courses</h3>
              <p className="text-2xl font-bold text-[#054581]">{counts ? animatedCourses : '—'}</p>
            </div>
          </div>
        </div>

        <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
          <div className="flex items-center">
            <FaUsers className="text-[#1086a4] text-2xl mr-4" />
            <div>
              <h3 className="text-sm font-medium text-gray-600">Learning Paths</h3>
              <p className="text-2xl font-bold text-[#054581]">{counts ? animatedPaths : '—'}</p>
            </div>
          </div>
        </div>
      </div>

      <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
        <h3 className="text-xl font-semibold text-[#054581] mb-4">Quick Actions</h3>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          <button className="flex items-center p-4 bg-gray-50 rounded-lg hover:bg-gray-100 transition border border-gray-200">
            <FaChartLine className="text-[#1086a4] mr-3" />
            <span className="text-gray-700">View Analytics</span>
          </button>
          <button className="flex items-center p-4 bg-gray-50 rounded-lg hover:bg-gray-100 transition border border-gray-200">
            <FaCalendarAlt className="text-[#1086a4] mr-3" />
            <span className="text-gray-700">Schedule Session</span>
          </button>
          <button className="flex items-center p-4 bg-gray-50 rounded-lg hover:bg-gray-100 transition border border-gray-200">
            <FaUsers className="text-[#1086a4] mr-3" />
            <span className="text-gray-700">Manage Students</span>
          </button>
        </div>
      </div>

      <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
        <h3 className="text-xl font-semibold text-[#054581] mb-4">Recent Activity</h3>
        <div className="text-gray-600">
          <p className="mb-2">• Student progress reports available</p>
          <p className="mb-2">• 5 new student enrollments this week</p>
          <p className="mb-2">• Content management moved to admin panel</p>
        </div>
      </div>
    </div>
  );

  const renderContent = () => {
    const active = contentTabs.find(t => t.key === activeContent);
    return (
      <div className="space-y-4">
        <h2 className="text-2xl font-bold text-[#054581]">My Content</h2>

        {/* Inner tabs */}
        <div className="flex border-b border-gray-200">
          {contentTabs.map(tab => (
            <button
              key={tab.key}
              onClick={() => setActiveContent(tab.key)}
              className={`flex items-center gap-2 px-5 py-3 text-sm font-medium border-b-2 transition-colors ${
                activeContent === tab.key
                  ? 'border-[#1086a4] text-[#1086a4]'
                  : 'border-transparent text-gray-500 hover:text-[#1086a4]'
              }`}
            >
              <tab.icon className="text-base" />
              {tab.label}
            </button>
          ))}
        </div>

        {/* List for active inner tab */}
        <div className="bg-white rounded-lg border border-gray-200 p-4">
          {active && (
            <ContentList
              key={active.key}
              endpoint={active.endpoint}
              viewPath={active.viewPath}
              icon={active.icon}
              label={active.label}
              authToken={authToken}
            />
          )}
        </div>
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-gray-50 pt-0 md:pt-8 px-2 sm:px-4 pb-20 md:pb-4">
      <div className="mx-auto bg-white rounded-lg shadow-sm flex flex-col md:flex-row">
        <DashboardSidebar activeTab={activeTab} onTabChange={setActiveTab} />
        <div className="flex-1 min-w-0 border-t md:border-t-0 md:border-l border-gray-200 p-6">
          {activeTab === 'Overview'          && renderOverview()}
          {activeTab === 'My Content'        && renderContent()}
          {activeTab === 'Student Progress'  && <StudentProgressTab />}
        </div>
      </div>
    </div>
  );
};

const mapStateToProps = state => ({ auth: state.auth });

export default connect(mapStateToProps)(DashboardContainer);
